import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Tooltip from '@mui/material/Tooltip'
import { useTheme } from '@mui/material/styles'
import { fmtCount } from '../StreamIngest/vocabulary'
import { ABOUT_WORDS, PUSH_KINDS, fmtShare, share } from './vocabulary'

// What each kind of push led to, one bar per kind: its pushes to a reporting
// phone as the whole, split into what each one went on to, deepest first —
// heard what the push was about, heard something else, visited and heard
// nothing, and no visit reported, the bar's recessive remainder. The outcomes
// nest (a listen is in a visit), so the parts are exclusive and the bar reads
// as how far a kind's pushes got; the shares beside it say the same in words,
// each of the whole.
//
// One hue in three ordinal steps, the deeper outcome the more salient: darker
// on light, brighter on dark. Each set was checked with the dataviz skill's
// validator (`--ordinal`) against this admin's panels, #f3f3f3 and #2d333b;
// re-check both if the theme's surfaces change. The table view behind the
// tab's toggle carries every number for whoever cannot tell the steps apart.

const STEPS = {
  light: { about: '#184f95', heard: '#2a78d6', visited: '#6da7ec' },
  dark: { about: '#86b6ef', heard: '#3987e5', visited: '#256abf' },
}

const BAR_PX = 20

const aboutWord = (row) => ABOUT_WORDS[row.about_is] || 'what it was about'

// A kind's counted pushes as exclusive parts, deepest first, the empty left
// out. A push about a person has nothing it was about, so its listens are
// just listens.
const partsOf = (row) => {
  const about = row.about ?? 0
  return [
    { key: 'about', n: about, label: `heard ${aboutWord(row)}` },
    {
      key: 'heard',
      n: row.heard - about,
      label: row.about_is ? 'heard something else' : 'heard a quote',
    },
    {
      key: 'visited',
      n: row.visited - row.heard,
      label: 'visited, heard nothing',
    },
    { key: 'none', n: row.counted - row.visited, label: 'no visit reported' },
  ].filter(({ n }) => n > 0)
}

export const LEGEND = [
  { key: 'about', label: 'heard what the push was about' },
  { key: 'heard', label: 'heard something else' },
  { key: 'visited', label: 'visited, heard nothing' },
  { key: 'none', label: 'no visit reported' },
]

const useFills = () => {
  const theme = useTheme()
  return { ...STEPS[theme.palette.mode], none: theme.palette.action.hover }
}

const Swatch = ({ fill }) => (
  <Box
    sx={{
      width: 10,
      height: 10,
      borderRadius: '2px',
      bgcolor: fill,
      flexShrink: 0,
    }}
  />
)

const Legend = () => {
  const fills = useFills()
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 1.5 }}>
      {LEGEND.map(({ key, label }) => (
        <Box
          key={key}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
        >
          <Swatch fill={fills[key]} />
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
        </Box>
      ))}
    </Box>
  )
}

// The bar: parts side by side, a 2px gap of the panel between each, square at
// the start and rounded at the end. A part as narrow as its gap still shows.
const Bar = ({ row }) => {
  const fills = useFills()
  const name = PUSH_KINDS[row.kind] || row.kind
  return (
    <Box
      sx={{
        display: 'flex',
        gap: '2px',
        height: BAR_PX,
        borderRadius: '0 4px 4px 0',
        overflow: 'hidden',
      }}
    >
      {partsOf(row).map(({ key, n, label }) => (
        <Tooltip
          key={key}
          title={`${name}: ${label}, ${fmtCount(n)} of ${fmtCount(
            row.counted
          )} (${fmtShare(share(n, row.counted))})`}
        >
          <Box sx={{ flex: `${n} 1 0`, minWidth: 2, bgcolor: fills[key] }} />
        </Tooltip>
      ))}
    </Box>
  )
}

// The shares in words, each of the kind's pushes to a reporting phone.
const Shares = ({ row }) => {
  const parts = [
    ['a visit', row.visited],
    ['a listen', row.heard],
    ...(row.about != null ? [[aboutWord(row), row.about]] : []),
  ]
  return (
    <Typography variant="body2" color="text.secondary">
      {parts.map(([label, n], index) => (
        <span key={label}>
          {index ? ' · ' : ''}
          {label}{' '}
          <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
            {fmtShare(share(n, row.counted))}
          </Box>
        </span>
      ))}
    </Typography>
  )
}

const PushFunnel = ({ kinds }) => (
  <Box>
    <Legend />
    {kinds.map((row) => (
      <Box
        key={row.kind}
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            mobile: '1fr',
            desktop: '220px minmax(0, 1fr) 320px',
          },
          columnGap: 3,
          rowGap: 0.75,
          alignItems: 'center',
          py: 1.25,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography variant="body2" fontWeight={700}>
            {PUSH_KINDS[row.kind] || row.kind}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {fmtCount(row.counted)} to a reporting phone · {fmtCount(row.sent)}{' '}
            sent
          </Typography>
        </Box>
        {row.counted > 0 ? (
          <Bar row={row} />
        ) : (
          <Typography variant="body2" color="text.secondary">
            none to a phone that reports yet
          </Typography>
        )}
        {row.counted > 0 ? <Shares row={row} /> : <Box />}
      </Box>
    ))}
  </Box>
)

export default PushFunnel
